import React, { useEffect } from 'react';
import './blog.css';
import { Figure, DiagramAsymmetry, DiagramModel, DiagramPipeline } from './dg';
import { DiagramAsk, DiagramWorlds, DiagramBridge } from './dg2';
import ArrowUpRight from '../components/ArrowUpRight';

export default function BusFactorBlog() {
    useEffect(() => {
        const prev = document.title;
        document.title = 'Your team’s bus factor is a graph problem — Hritik Kumar';
        return () => {
            document.title = prev;
        };
    }, []);

    return (
        <div className="blog">
            <p className="back">
                <a href="#/">← Hritik Kumar</a>
            </p>

            <header className="blog-head">
                <p className="kicker">Writing · October 2026 · 9 min read</p>
                <h1>Your team’s bus factor is a graph problem</h1>
                <p className="lead">
                    How BusFactor models who-knows-what in a repository — and lets
                    you rehearse losing a teammate before it happens.
                </p>
                <p className="blog-links">
                    <a
                        href="https://github.com/devnamrits/BusFactor"
                        target="_blank"
                        rel="noreferrer"
                    >
                        github <ArrowUpRight />
                    </a>{' '}
                    ·{' '}
                    <a
                        href="https://busfactor.onrender.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        live demo <ArrowUpRight />
                    </a>
                </p>
            </header>

            <article>
                <h2>Activity is measured. Knowledge isn’t.</h2>
                <p>
                    Every long-lived codebase has a quiet asymmetry:{' '}
                    <strong>activity is easy to measure, knowledge is not.</strong>{' '}
                    Your dashboard can tell you who commits the most lines. It
                    can’t tell you that one person is the only human who has ever
                    touched <em>why</em> the high-level REST client works the way
                    it does — or that an architecture decision points at a module
                    whose last maintainer just handed in their notice.
                </p>
                <p>
                    The bus factor is the number of people who can vanish before a
                    project stalls. Most teams treat it as a joke with a number
                    attached, because every tool they have measures the past:
                    commits, lines, review counts. None of those answer the
                    question that actually keeps a tech lead up at night:{' '}
                    <strong>
                        if this person leaves tomorrow, what breaks — and what do
                        we do about it?
                    </strong>
                </p>
                <p>
                    BusFactor is a working answer. You point it at a repository;
                    it ingests the history — commits, pull requests, docs,
                    decisions — into a knowledge graph on FalkorDB; and then it
                    gives you three verbs: <em>ask</em>, <em>drill</em>,{' '}
                    <em>heal</em>.
                </p>

                <Figure caption="A dashboard tells you who commits the most. It can’t tell you who the system can’t lose.">
                    <DiagramAsymmetry />
                </Figure>

                <h2>Knowledge is a relationship, not a number</h2>
                <p>
                    A spreadsheet can count commits. It can’t represent the fact
                    that{' '}
                    <strong>
                        shared work is what makes knowledge transferable
                    </strong>
                    : two people who have touched the same module, reviewed the
                    same pull requests, or written the same docs share something a
                    solo contributor doesn’t. That’s a graph — and modeling it as
                    anything else loses information exactly when it matters.
                </p>
                <p>
                    The model is small on purpose. Nodes:{' '}
                    <strong>
                        People, Modules, Pull Requests, Docs, Decisions
                    </strong>{' '}
                    — plus the question and answer nodes that make the assistant’s
                    memory auditable. Edges: <code>KNOWS</code> (weighted),{' '}
                    <code>AUTHORED</code>, <code>REVIEWED</code>,{' '}
                    <code>TOUCHES</code>, <code>WROTE</code>,{' '}
                    <code>DECIDED_BY</code>, <code>AFFECTS</code>,{' '}
                    <code>OWNS</code>.
                </p>

                <Figure caption="Nodes are people, modules, pull requests, docs and decisions; edges are the proof of how they touch each other. KNOWS carries a weight.">
                    <DiagramModel />
                </Figure>

                <p>
                    The interesting edge is <code>KNOWS</code>. It isn’t a
                    boolean; it decays and varies:
                </p>
                <pre>
                    <code>{`weight = 0.50 * commit_share   # this person's commits / all commits in the module
       + 0.30 * review_share   # reviews on PRs that touch the module
       + 0.20 * recency        # exponential decay since last activity`}</code>
                </pre>
                <p>
                    A <em>module</em> is a top-two-level path (
                    <code>client/rest-high-level</code>,{' '}
                    <code>modules/parent-join</code>) — deliberately not a file,
                    because files are noise and modules are where decisions live.
                    Trees like <code>docs/</code>, <code>build/</code>,{' '}
                    <code>deps/</code> and <code>.github/</code> are plumbing and
                    get excluded. Thresholds are pinned and recorded with every
                    snapshot: at least 3 commits for a module to exist in the
                    window, a meaningful share of a module for a person to be
                    recorded as knowing it, and a raw history window configurable
                    up to a decade.
                </p>
                <p>
                    Ingestion is deterministic — no LLM touches the data, and the
                    same clone at the same commit produces a byte-identical
                    snapshot, hash embedded in the graph metadata. If ingestion
                    hallucinated, everything downstream would be theater; so
                    ingestion doesn’t get to hallucinate.
                </p>

                <Figure caption="Git history in, a pinned snapshot, then one graph per repository — same inputs, byte-identical output.">
                    <DiagramPipeline />
                </Figure>

                <h2>The three verbs</h2>
                <h3>Ask</h3>
                <p>
                    A plain-language question (“who can we least afford to
                    lose?”) is routed to one of nine read-only graph tools:{' '}
                    <code>who_knows</code>, <code>critical_people</code>,{' '}
                    <code>knowledge_islands</code>, <code>module_risk</code>,{' '}
                    <code>simulate_departure</code>, <code>handover_plan</code>,{' '}
                    <code>heal_and_verify</code>, <code>recall</code>, and{' '}
                    <code>relations</code>. An LLM can pick <em>which</em> read
                    to run when it’s configured — but it never writes Cypher,
                    never touches the data, and the product is fully functional
                    with it switched off. Every numeric claim in an answer is
                    validated against the tool’s returned data before the answer
                    renders; the console shows a “numbers verified” badge and
                    foots every answer with who did the computing:{' '}
                    <em>the graph computed this answer; the model only picked
                    which read to run.</em>
                </p>

                <Figure caption="The model can pick a read. It cannot write Cypher, and it never touches the data.">
                    <DiagramAsk />
                </Figure>

                <h3>Drill</h3>
                <p>
                    The question no dashboard can answer — <em>what actually
                    breaks if this person leaves?</em> — gets answered the only
                    honest way: by removing them. BusFactor copies the main graph
                    into a throwaway world (<code>GRAPH.COPY</code>), performs the
                    surgery there, recomputes every metric on the real graph
                    engine, and diffs the two worlds into findings, each with a
                    proof: <code>sole_knower_lost</code> (a module just lost its
                    last recorded knower), <code>coverage_thin</code> (survivors
                    are down to the minimum), <code>orphaned_decision</code> (a
                    decision now references knowledge nobody in the team carries).
                </p>
                <p>
                    The real graph is never touched. On the Elasticsearch history
                    we loaded (194 people, 80 modules, 596 knowledge edges, 44
                    decisions), drilling one engineer takes 0.3 seconds and
                    produces the sentence the whole project exists to say:{' '}
                    <em>the high-level REST client loses its last recorded
                    knower.</em>
                </p>

                <h3>Heal</h3>
                <p>
                    A plan without verification is a suggestion. So BusFactor
                    drafts a handover plan — for each finding, a teacher chosen by
                    sibling-module mass and co-working history — names{' '}
                    <em>how</em> the transfer would work, previews it as a real
                    work order, and then re-runs the disaster with the fix in
                    place. The healed copy reports deltas, not vibes: stranded
                    components 1 → 0, zero-knower modules 1 → 0,{' '}
                    <em>
                        1 of 1 drill findings resolved · applied 2 projected
                        ownership edges
                    </em>
                    . And it lists what it could not fix, because a repair report
                    that can’t say “remaining: 3 orphaned decisions” isn’t a
                    report.
                </p>

                <Figure caption="The drill and heal worlds are copies — the graph you ship is never modified.">
                    <DiagramWorlds />
                </Figure>

                <div className="stats">
                    <div className="stat">
                        <b>0.3 s</b>
                        <span>
                            to rehearse one engineer’s departure on the
                            Elasticsearch history — copy, surgery, recompute
                        </span>
                    </div>
                    <div className="stat">
                        <b>1 → 0</b>
                        <span>
                            zero-knower modules after the handover plan is applied
                            and verified
                        </span>
                    </div>
                    <div className="stat">
                        <b>11 → 0</b>
                        <span>
                            stranded components healed on our own repo — with 3
                            orphaned decisions left, and reported
                        </span>
                    </div>
                </div>

                <h2>Where FalkorDB does the heavy lifting</h2>
                <p>
                    The what-if machinery is native to the database, not
                    simulated in Python: a drill is literally a second graph, and
                    a heal is a third. That means the risk model is recomputed by
                    the same engine that serves production queries — no second,
                    lying implementation in application code.
                </p>
                <p>
                    Connected components come from <code>algo.WCC</code>: after a
                    departure, anything no longer connected to the team’s main
                    knowledge component is stranded. That’s what “knowledge
                    islands” means operationally.
                </p>
                <p>
                    For handover planning, we use the graph to find{' '}
                    <em>bridges</em> — the shortest weighted path between a
                    candidate teacher and the losing module, walking shared
                    knowledge:
                </p>
                <pre>
                    <code>{`CALL algo.SPpaths({
  sourceNode: $teacher, targetNode: $departed,
  relTypes: ['KNOWS'], relDirection: 'both',
  weightProp: 'weight', pathCount: 1
})`}</code>
                </pre>
                <p>
                    A real finding from the Elasticsearch graph:
                </p>

                <Figure caption="The plan’s “why” is a shortest weighted path through shared work — Rene → rest-api-spec → Benjamin → the module.">
                    <DiagramBridge />
                </Figure>

                <p>
                    The plan doesn’t say “pair Rene with Benjamin because of
                    seniority”; it says <em>these two are connected through
                    shared work on the REST API spec, and that’s the transfer
                    channel</em>. (Small gotcha we hit and now document: on this
                    bipartite person/module view, <code>relDirection:
                    'outgoing'</code> returns zero rows for SPpaths;{' '}
                    <code>'both'</code> is what works.)
                </p>
                <p>
                    <strong>What we rejected, honestly.</strong> The first
                    version of the criticality score used PageRank. On a
                    bipartite person–module view it degenerated to near-uniform
                    weights — everyone’s connected to everything, so the
                    algorithm measured the graph’s shape rather than the team’s
                    dependency. We replaced it with a direct knowledge-mass
                    rollup: each person’s importance is the sum of the{' '}
                    <code>KNOWS</code> weights they hold. Less glamorous,
                    actually correct, and explainable in one sentence.
                </p>

                <h2>The FalkorDB surface, itemized</h2>
                <p>
                    Everything the product leans on, in one table — because
                    “we used a graph database” deserves receipts:
                </p>
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>FalkorDB feature</th>
                                <th>What it does here</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <code>multigraph</code>
                                </td>
                                <td>
                                    One instance, many isolated graphs: each
                                    repository gets its own graph (
                                    <code>repo:*</code>), and every what-if gets
                                    a disposable world (
                                    <code>sim:drill-*</code>,{' '}
                                    <code>sim:heal-*</code>) — tenant isolation
                                    without extra deployments.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>GRAPH.COPY</code>
                                </td>
                                <td>
                                    Clones a whole repository graph into the
                                    drill and heal worlds — milliseconds for the
                                    copy, and the production graph is never
                                    mutated. Worlds are cheap; that’s the
                                    feature the product is built on.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>algo.WCC</code>
                                </td>
                                <td>
                                    Connected components, used to detect
                                    stranded knowledge: after a simulated
                                    departure, anything no longer connected to
                                    the team’s main knowledge component is a
                                    knowledge island.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>algo.SPpaths</code>
                                </td>
                                <td>
                                    Shortest weighted path over{' '}
                                    <code>KNOWS</code> — the “why” behind every
                                    handover teacher pick (Rene →
                                    rest-api-spec → Benjamin → the module).
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>UNWIND</code>
                                </td>
                                <td>
                                    Batched writes with parameterized Cypher:
                                    ingestion creates nodes and edges one
                                    batch per round trip, and plan steps,
                                    memory citations, and mass updates ride the
                                    same pattern.
                                </td>
                            </tr>
                            <tr>
                                <td>weighted aggregation</td>
                                <td>
                                    Knowledge mass is a rollup over weighted{' '}
                                    <code>KNOWS</code> edges — the honest
                                    replacement for our PageRank attempt.
                                </td>
                            </tr>
                            <tr>
                                <td>graph-native memory</td>
                                <td>
                                    Every question and answer becomes nodes
                                    with <code>CITED</code> edges to what they
                                    touched — auditable, and tagged per repo
                                    and per world, so a healed world remembers
                                    healed answers.
                                </td>
                            </tr>
                            <tr>
                                <td>versioned reads</td>
                                <td>
                                    17 <code>.cypher</code> files in the repo
                                    behind the nine agent tools — the catalogue
                                    is auditable, and the model can only pick
                                    from it.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p>
                    None of this is a sidecar. Remove FalkorDB and the product
                    loses its data, its simulations, its proof, and its memory
                    — the graph is the substrate, not decoration.
                </p>

                <h2>Honesty is a feature</h2>
                <p>
                    The hardest part of an assistant like this is not making it
                    answer; it’s making it <em>refuse</em>. Three mechanisms keep
                    it honest:
                </p>
                <ol>
                    <li>
                        <strong>A fixed tool catalogue.</strong> The model can
                        only choose among the nine reads; free-form Cypher
                        generation is banned by design (audit, injection safety,
                        and the small matter of not letting a language model
                        invent join logic).
                    </li>
                    <li>
                        <strong>A number validator.</strong> Every answer template
                        is built from tool data, and a final pass checks that each
                        numeric claim appears in the returned data. Fabricated
                        figures don’t render.
                    </li>
                    <li>
                        <strong>Four outcome shapes.</strong> Answers are{' '}
                        <code>answered_with_path</code>,{' '}
                        <code>answered_from_memory</code>,{' '}
                        <code>not_in_graph</code>, or <code>error</code> — and
                        refusals are treated as demonstrations, not failures.
                    </li>
                </ol>
                <p>
                    My favorite test of that last point came from a real user
                    question about a real Elasticsearch contributor:{' '}
                    <em>“What does István Zoltán Szabó carry?”</em> The
                    interesting answer — the honest one — is:
                </p>
                <blockquote>
                    <p>
                        István Zoltán Szabó has no recorded module knowledge. What
                        the graph does hold: 2 doc page(s) they wrote (e.g.
                        “Reimplementing and extending the smartcn analyzer”).
                        Docs and build trees are plumbing in this index, not
                        knowledge modules; scattered code touches stay below the
                        significance floor.
                    </p>
                </blockquote>
                <p>
                    He has 454 commits in the window — but 1,063 of the 1,079
                    files he touched live under <code>docs/</code>, which this
                    index deliberately treats as plumbing, and his handful of
                    code commits amount to a rounding error inside enormous
                    modules. The graph’s first answer used to be a bare “no
                    recorded module knowledge,” which reads like a bug. Now it
                    says exactly what it knows, exactly what it doesn’t, and why
                    — and every “why” is clickable down to the two documentation
                    pages that are his real trail.
                </p>
                <p>
                    That’s the standard we hold the whole product to:{' '}
                    <strong>
                        the assistant is allowed to know things; it is not
                        allowed to guess.
                    </strong>
                </p>

                <h2>What real repositories look like</h2>
                <p>
                    We ship a benchmark (12 questions, from factual lookups to
                    adversarial what-ifs) and run it on every change; the honest
                    baseline — a naive retrieval demo — scores zero on anything
                    that requires simulating a removal, because there’s nothing
                    to retrieve: the answer doesn’t exist until the graph
                    computes it.
                </p>
                <p>
                    On the Elasticsearch history: bus factor <strong>1</strong> —
                    one departure strands knowledge. On our own repo, drilling
                    our most critical contributor produces 20 findings across
                    three channels, a 31-step handover plan with five teachers,
                    and a heal that takes stranded components from 11 to 0 and
                    the bus factor from 1 to 2 — leaving exactly three orphaned
                    decisions <em>unresolved and reported as such</em>.
                </p>

                <h2>Limitations, stated plainly</h2>
                <ul>
                    <li>
                        <strong>Reviews need a token.</strong> The knowledge
                        weight has a review component; without a GitHub token
                        (the default), it degrades gracefully to commits +
                        recency. The graph says what it had.
                    </li>
                    <li>
                        <strong>Docs are excluded from modules by design.</strong>{' '}
                        A documentation-heavy contributor can carry enormous real
                        value and zero module knowledge. The graph is explicit
                        about this instead of pretending otherwise.
                    </li>
                    <li>
                        <strong>Identity is a heuristic.</strong> Author strings
                        that normalize to the same name
                        (case/space/punctuation-insensitive) collapse into the
                        most active record; every
                        merge is logged and auditable.
                    </li>
                    <li>
                        <strong>Headline numbers are for reading together.</strong>{' '}
                        A drill can lower a module below one knower — at which
                        point it stops counting as a “minimum knowers” module
                        entirely and the headline bus factor can even{' '}
                        <em>rise</em>. Every surface therefore shows zero-knower
                        modules and stranded components next to the bus factor.
                        If a tool showed you only one number, you should distrust
                        it.
                    </li>
                </ul>

                <h2>Run it on your repo</h2>
                <p>
                    Prefer not to set anything up? The hosted demo runs at{' '}
                    <a
                        href="https://busfactor.onrender.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        busfactor.onrender.com
                    </a>{' '}
                    — both demo graphs are ready on first boot (free tier: the
                    first visit may take a minute to wake).
                </p>
                <pre>
                    <code>{`docker compose up -d            # falkordb + api + web
make ingest                     # load the pinned demo data
# console on http://localhost:5173 · API on http://localhost:8000
# then: paste any git URL (or a local clone path) into the console and index it`}</code>
                </pre>
                <p>
                    The same tool surface is exposed over MCP, so an agent can
                    call it directly:
                </p>
                <pre>
                    <code>{`claude mcp add busfactor -- uv run python -m busfactor.mcp_server`}</code>
                </pre>
                <p>
                    Every answer carries its path, every risk carries its fix,
                    and every simulated world is a copy you can throw away.
                    Because losing people is inevitable. Losing their knowledge
                    doesn’t have to be.
                </p>
            </article>

            <footer className="blog-foot">
                <p>
                    BusFactor was built for the WeMakeDevs × FalkorDB{' '}
                    <em>Graph Hacks</em> hackathon (October 2026). Source and
                    write-up:{' '}
                    <a
                        href="https://github.com/devnamrits/BusFactor"
                        target="_blank"
                        rel="noreferrer"
                    >
                        github.com/devnamrits/BusFactor
                    </a>
                    .
                </p>
            </footer>
        </div>
    );
}
